import {HttpEvent, HttpHandler, HttpInterceptor, HttpRequest} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Environment, SUPPORTED_LOCALES} from 'compob2b-libs';
import {Observable} from 'rxjs';
import {LocaleService} from './locale.service';
import {createIsBackendUrl} from '../../../server/backend-url';

/**
 * Adds `Accept-Language` header to all outgoing requests targeting the project
 * backend (`environment.serverApi`). Locale value is read from LocaleService;
 * persisted by the locale switcher in the customer header.
 */
@Injectable()
export class AcceptLanguageInterceptor implements HttpInterceptor {

  /** «URL ведёт на наш backend» — общий helper (backend-url.ts): корректно
   *  отсекает protocol-relative `//evil.com` и cross-origin, в отличие от
   *  наивного startsWith('/'). */
  private readonly isBackendUrl: (url: string) => boolean;

  constructor(
    private readonly localeService: LocaleService,
    @Inject('environment') environment: Environment,
  ) {
    let apiOrigin: string | null = null;
    try {
      const api = environment?.serverApi;
      apiOrigin = api ? new URL(api).origin : null;
    } catch { /* malformed serverApi → нет backend-origin */ }
    this.isBackendUrl = createIsBackendUrl(apiOrigin);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isBackendUrl(req.url)) {
      return next.handle(req);
    }
    if (req.headers.has('Accept-Language')) {
      return next.handle(req);
    }
    const locale = this.localeService.current;
    // Текущая локаль — с наибольшим весом, остальные поддерживаемые — фолбэком с
    // убывающим q. Масштабируется на любой состав SUPPORTED_LOCALES (для ru/en
    // даёт прежние `ru;q=0.9, en;q=0.5` / `en;q=0.9, ru;q=0.5`).
    const rest = SUPPORTED_LOCALES.filter(l => l !== locale);
    // q не опускаем ниже 0.1: q=0 means «неприемлемо», отрицательное — невалидно
    // (актуально при 6+ языках; для ru/en даёт прежние веса).
    const value = [`${locale};q=0.9`, ...rest.map((l, i) => `${l};q=${Math.max(0.1, 0.5 - i * 0.1).toFixed(1)}`)].join(', ');
    return next.handle(req.clone({headers: req.headers.set('Accept-Language', value)}));
  }
}
